import React, { useEffect, useMemo, useRef, useState } from 'react'
import { create } from 'zustand'
import { createPortal } from 'react-dom'
import { produce } from 'immer'
import { useHostedViewerCtx } from '@/hooks/use-app-ctx'
import { mergeClasses } from '@/helpers/styling'
import { allWidgetDefinitionsSorted } from '@/components/widgets'
import { getAreaByWidgetPath, mutateAreaByWidgetPath } from '@/components/editor/widget-tree'
import { saveViewerSnapshot } from '@/components/editor/widget-editor/undo-redo'
import { WidgetQueryStatus } from '@/components/editor/widget-editor/query-status'
import { QUERY_SIZES, QueryCard, QuerySize } from '@/components/editor/widget-editor/query-card'
import { AnyConfigDesc } from '@/components/editor/widget-editor/form-fields'
import { FloatingPlacement, FloatingStateContext, Panel } from '@/components/editor/panels/panel-registry'
import { PanelChrome } from '@/components/editor/panels/panel-chrome'
import { SizeHeaderIcons } from '@/components/editor/widget-editor/settings-size-icons'
import { closeQueryOverlay, QUERY_PANEL, resizeQuery } from './query-navigation'
import { useElementRect, widgetLayer } from './widget-outline-overlay'
import { createPortalLayer, PortalHiddenAnchor } from '@/shared/portal-layers'

const WIDTH = 640
const HEIGHT = 560
const MARGIN = 8

/** The widget's query card on top of the widget, or in a floating window */
export function QueryOverlay(props: {
  target: HTMLElement
  path: string
  status: WidgetQueryStatus
  size: "overlay" | "floating"
}) {
  const { path } = props
  const viewerCtx = useHostedViewerCtx()
  const config = getAreaByWidgetPath(path, viewerCtx.mainArea) as Record<string, unknown> | null
  const fields = useQueryFields(config?.widget as string | undefined)

  useEffect(function closeOnEscape() {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeQueryOverlay()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  // applies to the widget's config directly: the editor panel may be closed, or showing another widget
  const apply = (edits: Record<string, string>) => {
    const current = getAreaByWidgetPath(path, viewerCtx.mainArea)
    if (!current) return
    const updated = { ...current, ...edits }
    const mainArea = path === "mainArea"
      ? updated
      : produce(viewerCtx.mainArea, draft => {
        mutateAreaByWidgetPath(path, draft, updated)
      })
    saveViewerSnapshot(viewerCtx.updateMainArea(mainArea), `${Object.keys(edits).join(', ')} field change`)
  }

  if (!config) return null

  const title = `${props.target.dataset.widgetType} · query`
  const card = (size: QuerySize) => (
    <QueryCard size={size}
               path={path}
               status={props.status}
               fields={fields}
               applied={config}
               apply={apply}
               onResize={next => resizeQuery(path, next)} />
  )

  // a floating panel like the event log's: movable, resizable and full-screen capable
  return props.size === "floating" ? (
    <Panel id={QUERY_PANEL}
           role="floating-only"
           title={title}
           renderHeaderExtraIcons={() => (
             <SizeHeaderIcons current="floating" sizes={QUERY_SIZES} onResize={size => resizeQuery(path, size)} />
           )}
           onClose={closeQueryOverlay}
           defaultFloatingDimensions={{
             width: Math.min(1100, window.innerWidth - 124),
             height: Math.min(680, window.innerHeight - 124),
           }}>
      {card("floating")}
    </Panel>
  ) : (
    <OverlayFrame id="query"
                  target={props.target}
                  label={`${props.target.dataset.widgetType} query`}
                  header={<PanelChrome.Header title={title} onClose={closeQueryOverlay}>{title}</PanelChrome.Header>}
                  onDetach={placement => resizeQuery(path, "floating", placement)}>
      {card("overlay")}
    </OverlayFrame>
  )
}

/** Overlay sizes the user dragged to, per kind of overlay, kept while switching widgets */
const useOverlaySizeStore = create<Record<string, { width: number, height: number }>>()(() => ({}))

const MIN_WIDTH = 320
const MIN_HEIGHT = 240

/**
 * Positions its content on top of a widget, below the widget's chips. Resizable from its left and
 * bottom edges, up to the widget's own size (or the default size when the widget is smaller).
 * Dragging its header takes it off the widget: it follows the pointer, and on release becomes a
 * floating window at that spot.
 */
export function OverlayFrame(props: {
  id: "query" | "settings"
  target: HTMLElement
  label: string
  width?: number
  height?: number
  /** A panel header, in the docked (white) style of the floating windows' header */
  header: React.ReactNode
  onDetach: (placement: FloatingPlacement) => void
  children: React.ReactNode
}) {
  const rect = useElementRect(props.target)
  const chosen = useOverlaySizeStore(s => s[props.id])
  const [ dragOffset, setDragOffset ] = useState<{ x: number, y: number } | null>(null)
  const dragStart = useRef<{ x: number, y: number } | null>(null)
  if (!rect) return null

  // within the app area rather than the widget's own column
  const { visible } = rect
  const { app } = visible
  const top = Math.max(rect.top, visible.top + 11) + 16
  const defaultWidth = props.width ?? WIDTH
  const defaultHeight = props.height ?? HEIGHT
  const maxWidth = Math.min(Math.max(defaultWidth, rect.width - 2 * MARGIN), app.right - app.left - 2 * MARGIN)
  const maxHeight = Math.min(Math.max(defaultHeight, rect.top + rect.height - top - MARGIN), app.bottom - top - MARGIN)
  const width = clamp(chosen?.width ?? defaultWidth, Math.min(MIN_WIDTH, maxWidth), maxWidth)
  const height = clamp(chosen?.height ?? defaultHeight, Math.min(MIN_HEIGHT, maxHeight), maxHeight)
  const left = Math.min(
    Math.max(rect.left + rect.width - width - MARGIN, app.left + MARGIN),
    app.right - width - MARGIN,
  )

  const shown = { left: left + (dragOffset?.x ?? 0), top: top + (dragOffset?.y ?? 0) }
  const layer = widgetLayer(props.target)

  const resize = (next: { width: number, height: number }) => useOverlaySizeStore.setState({
    [props.id]: { width: clamp(next.width, MIN_WIDTH, maxWidth), height: clamp(next.height, MIN_HEIGHT, maxHeight) },
  })

  return createPortal((
    <OverlayRoot role="dialog"
                 aria-label={props.label}
                 className="position-absolute z-[202] flex flex-col overflow-hidden rounded-lg border-[1px] border-border bg-white0 font-sans shadow-[0_12px_32px_rgba(31,23,71,0.18)]"
                 style={{ top: shown.top - layer.origin.top, left: shown.left - layer.origin.left, width, height }}>
      <PortalHiddenAnchor />
      <div className="shrink-0 cursor-move touch-none"
           onPointerDown={e => {
             if (e.target instanceof Element && e.target.closest('button, a, input, select')) return
             e.currentTarget.setPointerCapture(e.pointerId)
             dragStart.current = { x: e.clientX, y: e.clientY }
           }}
           onPointerMove={e => {
             if (!dragStart.current) return
             const offset = { x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }
             // a click on the header is no drag
             if (!dragOffset && Math.hypot(offset.x, offset.y) < 4) return
             setDragOffset(offset)
           }}
           onPointerUp={e => {
             const start = dragStart.current
             dragStart.current = null
             if (!dragOffset || !start) return
             setDragOffset(null)
             // from the release itself, as the last move may not be rendered yet
             props.onDetach({
               x: left + e.clientX - start.x - window.scrollX,
               y: top + e.clientY - start.y - window.scrollY,
               width,
               height,
             })
           }}>
        {/* takes on the floating window's colours while dragged, as that is what it becomes on release */}
        <FloatingStateContext.Provider value={dragOffset ? "floating-active" : "docked"}>
          {props.header}
        </FloatingStateContext.Provider>
      </div>
      {props.children}
      <ResizeHandle edges="left" size={{ width, height }} onResize={resize} className="inset-y-0 left-0 w-1.5 cursor-ew-resize" />
      <ResizeHandle edges="bottom" size={{ width, height }} onResize={resize} className="inset-x-0 bottom-0 h-1.5 cursor-ns-resize" />
      <ResizeHandle edges="both" size={{ width, height }} onResize={resize} className="bottom-0 left-0 size-3 cursor-nesw-resize" />
    </OverlayRoot>
  ), layer.container)
}

/** Drags an overlay edge; the top right corner stays put, under the widget's chips */
function ResizeHandle(props: {
  edges: "left" | "bottom" | "both"
  size: { width: number, height: number }
  onResize: (size: { width: number, height: number }) => void
  className: string
}) {
  const start = useRef<{ x: number, y: number, width: number, height: number } | null>(null)
  return (
    <div aria-hidden
         className={mergeClasses("position-absolute z-30 hover:bg-[#51479e]/20", props.className)}
         onPointerDown={e => {
           e.preventDefault()
           e.currentTarget.setPointerCapture(e.pointerId)
           start.current = { x: e.clientX, y: e.clientY, ...props.size }
         }}
         onPointerMove={e => {
           if (!start.current) return
           const { x, y, width, height } = start.current
           props.onResize({
             width: props.edges === "bottom" ? width : width + x - e.clientX,
             height: props.edges === "left" ? height : height + e.clientY - y,
           })
         }}
         onPointerUp={() => { start.current = null }} />
  )
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

/** A portal layer like the panels', so popovers opened inside the overlay show on top of it */
const OverlayRoot = createPortalLayer(function OverlayRoot(props: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...props} />
})

/** The widget type's SPARQL fields, usually just one */
function useQueryFields(widget: string | undefined): AnyConfigDesc[] {
  return useMemo(() => {
    const definition = allWidgetDefinitionsSorted.find(w => w.name === widget)
    const variables = (definition?.variables ?? []) as ReadonlyArray<AnyConfigDesc>
    return variables.filter(v => v.type === "yasgui")
  }, [ widget ])
}
